import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { authApi } from '../../api/auth';
import { getApiErrorMessage } from '../../types/api';

const MIN_PASSWORD_LENGTH = 12;

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const tokenMissing = useMemo(() => token.length === 0, [token]);
  const passwordTooShort = useMemo(
    () => newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH,
    [newPassword],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (tokenMissing) {
      setError('Der Reset-Link ist ungueltig oder unvollstaendig.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Die Passwoerter stimmen nicht ueberein.');
      return;
    }

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Das Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword(token, newPassword);
      setDone(true);
      setInfo('Passwort erfolgreich geaendert. Sie koennen sich jetzt anmelden.');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Passwort konnte nicht geaendert werden.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Passwort zuruecksetzen</h2>
          <p className="mt-2 text-center text-sm text-gray-600">FeWo-Monitor</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
        )}
        {info && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">{info}</div>
        )}

        {done ? (
          <div className="text-center">
            <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">
              Zur Anmeldung
            </Link>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="new-password" className="sr-only">
                Neues Passwort
              </label>
              <input
                id="new-password"
                name="new-password"
                type="password"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Neues Passwort"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                Mindestens {MIN_PASSWORD_LENGTH} Zeichen.
              </p>
            </div>

            <div>
              <label htmlFor="confirm-password" className="sr-only">
                Passwort bestaetigen
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Passwort bestaetigen"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || tokenMissing || passwordTooShort}
                className="btn btn-primary group relative w-full flex justify-center border border-transparent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {loading ? 'Wird gespeichert...' : 'Passwort speichern'}
              </button>
            </div>

            <div className="text-center">
              <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">
                Zurueck zur Anmeldung
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
